import { useState } from "react";
import { api } from "../api";
// loginMode: "key" | "user"

export default function Login({ onLogin, onBack, initialExpired = false, onExpiredDismiss }) {
  const [loginMode, setLoginMode] = useState("key"); // "key" | "user"
  const [key,     setKey]     = useState("");
  const [needCode, setNeedCode] = useState(false); // admin key with 2FA on: also ask for a code
  const [keyCode, setKeyCode] = useState("");
  const [username, setUsername] = useState("");
  const [userPw,  setUserPw]  = useState("");
  const [otp,     setOtp]     = useState("");
  const [error,   setError]   = useState(initialExpired ? "EXPIRED" : "");
  const [loading, setLoading] = useState(false);
  const [pendingActivation, setPendingActivation] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [oldPhrase, setOldPhrase] = useState("");
  const [activatedData, setActivatedData] = useState(null); // holds token+recovery_phrase after activation
  const [copied, setCopied] = useState(false);

  const handleSubmit = async () => {
    if (!key.trim()) return;
    setLoading(true); setError("");
    try {
      const data = await api.login(key.trim(), needCode ? keyCode.trim() : undefined);
      if (data.needs_activation) {
        setPendingActivation(true);
      } else {
        onLogin(data);
      }
    } catch (e) {
      if (e.subscriptionExpired) {
        setError("EXPIRED");
      } else if (e.message === "ADMIN_CODE_REQUIRED") {
        setNeedCode(true);
        setError("");
      } else if (e.message === "TOO_MANY_ATTEMPTS") {
        setError("Too many tries. Wait 5 minutes and try again.");
      } else {
        setError(e.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async (transferPhrase) => {
    setLoading(true); setError("");
    try {
      const data = await api.activate(key.trim(), transferPhrase || undefined);
      if (data.transferred) {
        // Settings migrated — go straight to dashboard
        onLogin(data);
      } else {
        setActivatedData(data);
      }
    } catch (e) {
      if (e.message === "RECOVERY_PHRASE_NOT_FOUND") {
        setError("Recovery phrase not found. Check it and try again.");
      } else {
        setError(e.message);
        setPendingActivation(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const copyPhrase = () => {
    navigator.clipboard.writeText(activatedData.recovery_phrase);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const finishActivation = () => {
    onLogin(activatedData);
  };

  // Screen: subscription expired
  if (error === "EXPIRED") {
    return (
      <div style={styles.wrap}>
        <div style={styles.box}>
          <div style={styles.brand}>RBXWar</div>
          <div style={{ fontSize: 40, marginBottom: 16 }}>⏰</div>
          <h1 style={styles.title}>Subscription expired</h1>
          <p style={styles.sub}>
            Your 30-day subscription has ended. Purchase a new key to continue. Your settings and accounts are saved.
          </p>
          <button
            style={styles.btn}
            onClick={() => window.open("https://rbxwar.mysellauth.com/", "_blank")}
          >
            Buy a new key →
          </button>
          <button style={styles.back} onClick={() => { setError(""); onExpiredDismiss?.(); }}>
            ← Enter a new key
          </button>
        </div>
      </div>
    );
  }

  // Screen 3: recovery phrase reveal, after activation completes
  if (activatedData) {
    return (
      <div style={styles.wrap}>
        <div style={styles.box}>
          <div style={styles.brand}>RBXWar</div>
          <h1 style={styles.title}>Save your recovery phrase</h1>
          <p style={styles.sub}>
            This is your backup credential. Use it to log in if you ever lose your
            activation key. It won't be shown again.
          </p>
          <div style={styles.phraseBox}>
            <code style={styles.phraseText}>{activatedData.recovery_phrase}</code>
          </div>
          <button style={styles.copyBtn} onClick={copyPhrase}>
            {copied ? "✓ Copied!" : "Copy Recovery Phrase"}
          </button>
          <div style={styles.warning}>⚠️ Store this somewhere safe. Anyone with this phrase can access your account.</div>
          <button style={styles.btn} onClick={finishActivation}>
            Activate
          </button>
        </div>
      </div>
    );
  }

  // Screen 2: confirm first-time activation
  if (pendingActivation) {
    if (showTransfer) {
      return (
        <div style={styles.wrap}>
          <div style={styles.box}>
            <div style={styles.brand}>RBXWar</div>
            <h1 style={styles.title}>Transfer existing account</h1>
            <p style={styles.sub}>
              Enter your old recovery phrase to migrate your Roblox accounts, settings, and config to this new key. Your subscription timer resets from today.
            </p>
            <input
              style={styles.input}
              placeholder="old recovery phrase..."
              value={oldPhrase}
              onChange={e => setOldPhrase(e.target.value)}
              autoFocus
            />
            {error && <p style={styles.error}>{error}</p>}
            <button style={styles.btn} onClick={() => handleActivate(oldPhrase)} disabled={loading || !oldPhrase.trim()}>
              {loading ? "Transferring..." : "Transfer & Activate →"}
            </button>
            <button style={styles.back} onClick={() => { setShowTransfer(false); setError(""); }}>
              ← Back
            </button>
          </div>
        </div>
      );
    }

    return (
      <div style={styles.wrap}>
        <div style={styles.box}>
          <div style={styles.brand}>RBXWar</div>
          <h1 style={styles.title}>First time using this key</h1>
          <p style={styles.sub}>
            Activating starts your subscription period now. You'll also get a
            recovery phrase, a backup way to log in if you ever lose this key.
          </p>
          {error && <p style={styles.error}>{error}</p>}
          <button style={styles.btn} onClick={() => handleActivate()} disabled={loading}>
            {loading ? "Activating..." : "Activate (fresh start)"}
          </button>
          <button
            style={{...styles.btn, background:"#1a1a1a", color:"#888", border:"1px solid #2a2a2a", marginTop:8}}
            onClick={() => { setShowTransfer(true); setError(""); }}
          >
            Transfer from old account →
          </button>
          <button style={styles.back} onClick={() => setPendingActivation(false)}>
            ← Back
          </button>
        </div>
      </div>
    );
  }

  const handleUserLogin = async () => {
    if (!username.trim() || !userPw || !otp.trim()) { setError("Enter your username, password and 2FA code."); return; }
    setLoading(true); setError("");
    try {
      const data = await api.loginPassword(username.trim(), userPw, otp.trim());
      onLogin(data);
    } catch (e) {
      if (e.subscriptionExpired) setError("EXPIRED");
      else if (e.message === "TOO_MANY_ATTEMPTS") setError("Too many tries. Wait 5 minutes, or log in with your key.");
      else setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  // Screen 1: enter key, or username + password + 2FA code
  return (
    <div style={styles.wrap}>
      <div style={styles.box}>
        <div style={styles.brand}>RBXWar</div>

        {/* Mode toggle */}
        <div style={styles.modeRow}>
          <button style={{...styles.modeBtn, ...(loginMode === "key"   ? styles.modeBtnActive : {})}} onClick={() => { setLoginMode("key");   setError(""); }}>🔑 Key</button>
          <button style={{...styles.modeBtn, ...(loginMode === "user"  ? styles.modeBtnActive : {})}} onClick={() => { setLoginMode("user");  setError(""); }}>👤 Username</button>
        </div>

        {loginMode === "key" ? <>
          <h1 style={styles.title}>Enter your key</h1>
          <p style={styles.sub}>Paste your activation key (or recovery phrase) below to access the dashboard.</p>
          <input
            style={styles.input}
            placeholder="Enter your key or recovery phrase"
            value={key}
            onChange={e => setKey(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleSubmit()}
            autoFocus={!needCode}
          />
          {needCode && (
            <>
              <p style={{ ...styles.sub, margin: "2px 0 8px" }}>2FA is on for this key. Enter the 6-digit code from your authenticator app (or a backup code).</p>
              <input
                style={{ ...styles.input, letterSpacing: 2 }}
                placeholder="6-digit code or backup code"
                autoComplete="one-time-code"
                value={keyCode}
                onChange={e => setKeyCode(e.target.value)}
                onKeyDown={e => e.key === "Enter" && handleSubmit()}
                autoFocus
              />
            </>
          )}
          {error && <p style={styles.error}>{error}</p>}
          <button style={styles.btn} onClick={handleSubmit} disabled={loading}>
            {loading ? "Verifying..." : needCode ? "Verify and continue" : "Continue"}
          </button>
        </> : <>
          <h1 style={styles.title}>Sign in</h1>
          <p style={styles.sub}>Enter your username, password and the 6-digit code from your authenticator app (or a backup code).</p>
          <input
            style={{...styles.input, letterSpacing: 0, marginBottom: 8}}
            placeholder="Username"
            autoComplete="username"
            value={username}
            onChange={e => setUsername(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleUserLogin()}
            autoFocus
          />
          <input
            style={{...styles.input, letterSpacing: 0, marginBottom: 8}}
            placeholder="Password"
            type="password"
            autoComplete="current-password"
            value={userPw}
            onChange={e => setUserPw(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleUserLogin()}
          />
          <input
            style={{...styles.input, letterSpacing: 2}}
            placeholder="6-digit code or backup code"
            autoComplete="one-time-code"
            value={otp}
            onChange={e => setOtp(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleUserLogin()}
          />
          {error && <p style={styles.error}>{error}</p>}
          <button style={styles.btn} onClick={handleUserLogin} disabled={loading}>
            {loading ? "Signing in..." : "Sign in"}
          </button>
        </>}

        {onBack && (
          <button style={styles.back} onClick={onBack}>
            ← Back
          </button>
        )}
      </div>
    </div>
  );
}

const styles = {
  wrap:      { minHeight: "calc(100vh / var(--ui-zoom, 1))", background: "#080808", display: "flex", alignItems: "center", justifyContent: "center" },
  box:       { background: "#0e0e0e", border: "1px solid #1a1a1a", borderRadius: 14, padding: "44px 52px", width: 420, textAlign: "center" },
  brand:     { fontSize: 13, fontWeight: 700, letterSpacing: "3px", textTransform: "uppercase", color: "var(--rbx-accent, #38bdf8)", marginBottom: 24 },
  title:     { color: "#fff", fontSize: 24, fontWeight: 800, fontFamily: "inherit", margin: "0 0 10px", letterSpacing: "-0.5px" },
  sub:       { color: "#444", fontSize: 14, margin: "0 0 28px", lineHeight: 1.6 },
  input:     { width: "100%", background: "#141414", border: "1px solid #222", borderRadius: 8, padding: "13px 16px", color: "#fff", fontSize: 14, fontFamily: "monospace", outline: "none", boxSizing: "border-box", letterSpacing: 2 },
  error:     { color: "#f87171", fontSize: 13, margin: "10px 0 0" },
  btn:       { width: "100%", marginTop: 16, padding: "13px 0", background: "var(--rbx-accent, #38bdf8)", border: "none", borderRadius: 8, color: "#000", fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" },
  back:      { marginTop: 16, background: "none", border: "none", color: "#333", fontSize: 13, cursor: "pointer", fontFamily: "inherit" },
  modeRow:   { display:"flex", gap:6, marginBottom:20, background:"#111", borderRadius:8, padding:4 },
  modeBtn:   { flex:1, padding:"8px 0", background:"transparent", border:"none", borderRadius:6, color:"#555", cursor:"pointer", fontSize:13, fontWeight:600, fontFamily:"inherit" },
  modeBtnActive: { background:"#1a1a1a", color:"var(--rbx-accent, #38bdf8)" },
  phraseBox: { background: "#141414", border: "1px solid var(--rbx-accent, #38bdf8)", borderRadius: 8, padding: "16px 12px", marginBottom: 14, wordBreak: "break-word" },
  phraseText:{ color: "var(--rbx-accent, #38bdf8)", fontSize: 16, fontWeight: 700, letterSpacing: 1, lineHeight: 1.6 },
  copyBtn:   { width: "100%", padding: "11px 0", background: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: 8, color: "#ccc", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", marginBottom: 14 },
  warning:   { color: "#f59e0b", fontSize: 12, lineHeight: 1.6, marginBottom: 4, textAlign: "left" },
};
